function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
const isNonEmpty = s => typeof s === 'string' && s.trim().length > 0;
const UNSAFE_OBJECT_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
const isSafeOwnObjectKey = key => typeof key === 'string' && key.length > 0 && !UNSAFE_OBJECT_KEYS.has(key);
const isCopyableSavedViewsSource = value => value != null && typeof value === 'object' && !Array.isArray(value);

/** Collect own enumerable keys from source into a null-prototype map. */
const collectOwnSavedViewEntries = source => {
  const entries = Object.create(null);
  if (!isCopyableSavedViewsSource(source)) {
    return entries;
  }
  Object.keys(source).forEach(key => {
    if (isSafeOwnObjectKey(key) && Object.prototype.hasOwnProperty.call(source, key)) {
      entries[key] = source[key];
    }
  });
  return entries;
};

/** User savedViews first; preferences savedViews override on duplicate keys. */
const mergeSavedViews = (savedViews, preferencesSavedViews) => _objectSpread(_objectSpread({}, collectOwnSavedViewEntries(savedViews)), collectOwnSavedViewEntries(preferencesSavedViews));

/** `defaultLandingPage.key` for kind `savedView` (matches userPreferences form). */
export const buildSavedViewDefaultLandingKey = (cabinetIdentifier, filterId) => `${String(cabinetIdentifier)}__${String(filterId)}`;

/** Reject non-internal navigation targets. */
export const isSafeInternalAppPath = u => typeof u === 'string' && u.startsWith('/') && !u.startsWith('//') && !u.includes('://');
const getFavoriteEntry = (preferencesContent, favoriteKey) => {
  if (!favoriteKey || !isSafeOwnObjectKey(favoriteKey)) return null;
  const raw = preferencesContent === null || preferencesContent === void 0 ? void 0 : preferencesContent.favorites;
  if (!raw) return null;
  if (Array.isArray(raw)) {
    return raw.find(f => f && String(f.id) === String(favoriteKey) && !f.deletedAt) || null;
  }
  if (typeof raw !== 'object' || !Object.prototype.hasOwnProperty.call(raw, favoriteKey)) {
    return null;
  }
  const entry = raw[favoriteKey];
  if (!entry || entry.deletedAt) return null;
  return entry;
};
const findSavedViewByCompositeKey = (savedViews, compositeKey) => {
  if (!savedViews || typeof savedViews !== 'object' || !compositeKey) return null;
  if (!isSafeOwnObjectKey(compositeKey)) return null;
  const direct = savedViews[compositeKey];
  if (direct && typeof direct === 'object' && direct.cabinetIdentifier != null && direct.filterId != null) {
    return {
      cabinetIdentifier: String(direct.cabinetIdentifier),
      filterId: String(direct.filterId)
    };
  }
  const keys = Object.keys(savedViews);
  for (let i = 0; i < keys.length; i += 1) {
    const mapKey = keys[i];
    const row = savedViews[mapKey];
    if (row && typeof row === 'object') {
      const ci = row.cabinetIdentifier;
      const fi = row.filterId;
      if (ci != null && fi != null && `${String(ci)}__${String(fi)}` === compositeKey) {
        return {
          cabinetIdentifier: String(ci),
          filterId: String(fi)
        };
      }
    }
  }
  for (let i = 0; i < keys.length; i += 1) {
    const cabinetIdentifier = keys[i];
    const byFilter = savedViews[cabinetIdentifier];
    if (byFilter && typeof byFilter === 'object') {
      const filterIds = Object.keys(byFilter);
      for (let j = 0; j < filterIds.length; j += 1) {
        const filterId = filterIds[j];
        if (`${cabinetIdentifier}__${filterId}` === compositeKey) {
          const row = byFilter[filterId];
          if (row && typeof row === 'object') {
            return {
              cabinetIdentifier,
              filterId
            };
          }
        }
      }
    }
  }
  return null;
};

/**
 * @param {{
 *   defaultLandingPage?: { kind?: string, key?: string },
 *   preferencesContent?: Record<string, unknown>,
 *   savedViews?: Record<string, unknown>,
 * }} args
 * For kind savedView, resolves against merged savedViews: User store plus
 * preferencesContent.savedViews (preferences keys win on duplicates).
 * @returns {string|null} pathname for hash router (e.g. /cabinets/foo), or null to use fallback
 */
export const resolveDefaultLandingPageUrl = ({
  defaultLandingPage,
  preferencesContent: _preferencesContent = {},
  savedViews: _savedViews = {}
}) => {
  const kind = defaultLandingPage === null || defaultLandingPage === void 0 ? void 0 : defaultLandingPage.kind;
  const key = defaultLandingPage === null || defaultLandingPage === void 0 ? void 0 : defaultLandingPage.key;
  if (!isNonEmpty(kind) || !isNonEmpty(key)) return null;
  const mergedSavedViews = mergeSavedViews(_savedViews, _preferencesContent === null || _preferencesContent === void 0 ? void 0 : _preferencesContent.savedViews);
  if (kind === 'favorite') {
    const fav = getFavoriteEntry(_preferencesContent, key.trim());
    const url = fav === null || fav === void 0 ? void 0 : fav.url;
    return isSafeInternalAppPath(url) ? url : null;
  }
  if (kind === 'savedView') {
    const composite = key.trim();
    if (!isSafeOwnObjectKey(composite)) return null;
    const found = findSavedViewByCompositeKey(mergedSavedViews, composite);
    if (!found) return null;
    const {
      cabinetIdentifier,
      filterId
    } = found;
    const path = `/cabinets/${cabinetIdentifier}?view=saved&filterId=${encodeURIComponent(filterId)}`;
    return isSafeInternalAppPath(path) ? path : null;
  }
  return null;
};